import React, { useState, useEffect } from "react";
import { BASE_URL } from "../constants";
import StaffSidebar from "./StaffSidebar";
import StaffHeader from "./StaffHeader";

const StaffServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedService, setExpandedService] = useState(null);

  useEffect(() => {
    fetchServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("staffToken");
      const res = await fetch(`${BASE_URL}staff/services`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      const data = await res.json();
      if (data?.status === "success") {
        setServices(data.data?.services || []);
      } else {
        setError(data?.message || "Failed to load services");
      }
    } catch (err) {
      console.error(err);
      setError(err.message || "Network error");
    } finally {
      setLoading(false);
    }
  };

  const toggle = (id) => setExpandedService(expandedService === id ? null : id);

  return (
    <div className="flex min-h-screen bg-gray-100">
      <StaffSidebar
        onLogout={() => {
          localStorage.removeItem("staffToken");
          window.location.href = "/login";
        }}
      />
      <div className="flex-1">
        <StaffHeader title="Services" />
        <main className="p-6">
          {loading && (
            <div className="flex items-center justify-center min-h-[300px]">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent" />
            </div>
          )}

          {error && (
            <div className="max-w-3xl mx-auto">
              <div className="bg-red-50 border border-red-200 rounded p-4">
                <p className="text-red-700">{error}</p>
                <div className="mt-3">
                  <button
                    onClick={fetchServices}
                    className="px-3 py-1 bg-red-600 text-white rounded"
                  >
                    Retry
                  </button>
                </div>
              </div>
            </div>
          )}

          {!loading && !error && (
            <div className="max-w-6xl mx-auto space-y-6">
              {services.length === 0 ? (
                <div className="text-center py-16">
                  <p className="text-gray-600">No services available</p>
                </div>
              ) : (
                services.map((service) => (
                  <div
                    key={service.id}
                    className="bg-white rounded-lg shadow overflow-hidden"
                  >
                    <div
                      className="p-4 flex items-center justify-between cursor-pointer"
                      onClick={() => toggle(service.id)}
                    >
                      <div className="flex items-center space-x-4">
                        <img
                          src={service.imageUrl}
                          alt={service.name}
                          className="h-16 w-16 rounded object-cover"
                        />
                        <div>
                          <div className="flex items-center space-x-2">
                            <h3 className="text-lg font-semibold">
                              {service.name}
                            </h3>
                            <span
                              className={`text-xs px-2 py-0.5 rounded ${
                                service.isActive
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {service.isActive ? "Active" : "Inactive"}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">
                            {service.description}
                          </p>
                        </div>
                      </div>
                      <div className="text-gray-400">
                        {expandedService === service.id ? "▴" : "▾"}
                      </div>
                    </div>

                    {expandedService === service.id && (
                      <div className="border-t p-4 bg-gray-50">
                        <h4 className="font-medium mb-3">Sub-services</h4>
                        <div className="space-y-3">
                          {service.subServices?.map((ss) => (
                            <div
                              key={ss.id}
                              className="p-3 bg-white border rounded flex items-start gap-4"
                            >
                              <img
                                src={ss.imageUrl}
                                alt={ss.name}
                                className="h-12 w-12 rounded object-cover"
                              />
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <h5 className="font-medium">{ss.name}</h5>
                                    <p className="text-sm text-gray-500 mt-1">
                                      {ss.description}
                                    </p>
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    {ss.priceType}
                                  </div>
                                </div>

                                {ss.cityPricing?.length > 0 && (
                                  <div className="mt-3 overflow-x-auto">
                                    <table className="min-w-full text-sm">
                                      <thead>
                                        <tr className="text-left text-gray-500">
                                          <th className="pr-4">City</th>
                                          <th className="pr-4">Zone</th>
                                          <th className="pr-4">2hr</th>
                                          <th className="pr-4">4hr</th>
                                          <th className="pr-4">6hr</th>
                                          <th className="pr-4">8hr</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {ss.cityPricing.map((cp) => (
                                          <tr key={cp.id} className="border-t">
                                            <td className="py-2 pr-4">
                                              {cp.city}
                                            </td>
                                            <td className="py-2 pr-4">
                                              {cp.zone}
                                            </td>
                                            <td className="py-2 pr-4">
                                              ₹
                                              {cp.hourlyPricing?.["2hr"] ?? "-"}
                                            </td>
                                            <td className="py-2 pr-4">
                                              ₹
                                              {cp.hourlyPricing?.["4hr"] ?? "-"}
                                            </td>
                                            <td className="py-2 pr-4">
                                              ₹
                                              {cp.hourlyPricing?.["6hr"] ?? "-"}
                                            </td>
                                            <td className="py-2 pr-4">
                                              ₹
                                              {cp.hourlyPricing?.["8hr"] ?? "-"}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default StaffServices;
